import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { CareActions } from './care.actions';
import { AuthActions } from '@app/store/auth/auth.actions';
import { CareLinksApiService } from '@app/core/api/care-links';
import { LoggerService, ToastNotificationService } from '@app/core/infrastructure';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CareEffects {
  private readonly actions$ = inject(Actions);
  private readonly careLinksApi = inject(CareLinksApiService);
  private readonly logger = inject(LoggerService).scope('CareEffects');
  private readonly toast = inject(ToastNotificationService);
  private readonly translate = inject(TranslateService);

  loadCareRecipients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareActions.loadCareRecipients),
      switchMap(() =>
        this.careLinksApi.getMyCareRecipients().pipe(
          map(careRecipients => CareActions.loadCareRecipientsSuccess({ careRecipients })),
          catchError(error => {
            this.logger.error('Load care recipients failed:', error);
            return of(CareActions.loadCareRecipientsFailure({ error: error.message }));
          })
        )
      )
    )
  );

  revokeLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareActions.revokeLink),
      switchMap(({ linkId }) =>
        this.careLinksApi.revokeLink(linkId).pipe(
          map(response => CareActions.revokeLinkSuccess({ response })),
          catchError(error => {
            this.logger.error('Revoke link failed:', error);
            return of(CareActions.revokeLinkFailure({ error: error.message }));
          })
        )
      )
    )
  );

  revokeLinkSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareActions.revokeLinkSuccess),
      tap(() => this.toast.showSuccess(this.translate.instant('care.recipients.unlinkSuccess'))),
      map(() => CareActions.loadCareRecipients())
    )
  );

  revokeLinkFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CareActions.revokeLinkFailure),
      tap(({ error }) => this.toast.showError(error)),
    ),
    { dispatch: false }
  );

  clearOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => CareActions.clearCareState())
    )
  );

  logSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CareActions.loadCareRecipientsSuccess,
        CareActions.revokeLinkSuccess
      ),
      tap(action => this.logger.debug('Success:', action.type))
    ),
    { dispatch: false }
  );


}
